/**
 * The app, mounted. Everything `main.tsx` used to do, one dynamic import later:
 * it runs only after `hydrateStorage` has filled the document mirror, so the
 * store's synchronous restore below finds its library. See `main.tsx`.
 */

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App'
import { useStore } from './core/store'
import { pulses } from './core/pulse'
import { initializeDurablePersistence, installPersistence } from './core/autosave'
import { installPassTimes } from './core/passTime'
import { installPassDraws } from './core/passDraw'
import { repairDemo } from './engine/demo'
import { DurabilityNotice } from './ui/DurabilityNotice'
import { Announcer } from './ui/Announcer'
import {
  branchMarkdown,
  briefingMarkdown,
  explorationMarkdown,
  reportMarkdown,
} from './core/markdown'
import { manifestJson } from './core/manifest'

// A graph tool is easier to debug from the console than from a breakpoint, and
// a document generator is easier to read than to diff.
if (import.meta.env.DEV) {
  const w = window as unknown as { nv: unknown; nvmd: unknown; nvPulses: unknown }
  w.nv = useStore
  // The pulse clock beside it, because it is no longer reachable through the
  // store: `scripts/perf-audit.mjs` marks it to measure what a beat costs.
  w.nvPulses = pulses
  // All five documents, not the two this hook shipped with. The report and the
  // briefing are the ones whose wording is hardest to get right, so they are
  // exactly the ones worth being able to print in a console — and the manifest is
  // here for the same stated reason one step further: its key ORDER and its
  // truncation flag are the parts hardest to eyeball anywhere else, and a diff of
  // two builds is exactly how you check them. Called bare it reports `null` for
  // the reading and for both calibration tables, which is the honest answer to a
  // console that passed neither.
  w.nvmd = { branchMarkdown, explorationMarkdown, reportMarkdown, briefingMarkdown, manifestJson }
}

// Outside React on purpose: StrictMode would install the subscription twice
// and tear the first one down on the first unmount. It lives for the life of
// the page, so the teardown is deliberately discarded.
await initializeDurablePersistence()
installPersistence()
// The same reason, and the same lifetime: every pass this page runs and lands
// adds its seconds to the record "usually" is read from (`core/passTime.ts`).
installPassTimes(useStore)
// And its tokens, to the record the figure before a press is drawn from once a
// kind of pass has run here on a model (`core/passDraw.ts`, D311).
installPassDraws(useStore)
// After the autosave is listening, so a worked example put back here is kept
// like any edit; before the first render, so nobody sees the stub it replaces.
// See `repairDemo`.
repairDemo()

// The provider adapters are loaded on demand, so a stale deploy makes one of them
// 404 — and without this the failure lands as a silent no-op on the user's first
// paid pass, on the one action they were most sure they had paid for.
window.addEventListener('vite:preloadError', () => location.reload())

const root = document.getElementById('root')
if (!root) throw new Error('Missing #root')

// The announcer sits beside the app rather than in it, for the reason the h1
// sits outside `main.stage`: a sheet makes the stage inert, and an inert live
// region is a silent one. See `ui/Announcer.tsx`.
createRoot(root).render(
  <StrictMode>
    <App />
    <DurabilityNotice />
    <Announcer />
  </StrictMode>,
)
